﻿<script setup lang="ts">
    import { computed, onMounted, ref, watch } from 'vue';
    import useShow from '@/shared/composables/useShow';
    import { createCodeSetPublishingRelease, getCodeSetSubscriptions } from '@/shared/helpers/api.ts';
    import { http } from '@/shared/httpWrapper';
    import { CodeSetSubscriptions } from '@/shared/helpers/api/types/entities.ts';
    import CodeEditSaveProgress from '@/shared/components/CodeEditSaveProgress.vue';
    import GenericDropdownMenu from '@/shared/components/GenericDropdownMenu.vue';
    import { requireCanEditCode as vRequireCanEditCode } from '@/shared/directives/requirePermission';
    import { FacilityPublishEligibility, getFacilityPublishEligibilityList } from '@/shared/helpers/facilityHelpers.ts';
    import MultiScopeSelector from '@/shared/components/scopeselector/MultiScopeSelector.vue';

    const { showError } = useShow();

    type Filters = {
        regEx: string[];
        queries: string[][];
    };

    type Props = {
        libraryName: string;
        codeSetName: string;
        filteredCodeCount: number;
        codeFilter: Filters;
    };

    const props = defineProps<Props>();

    const showModal = ref(false);
    const isLoading = ref(false);
    const isSubmitting = ref(false);
    const showPublishProgress = ref(false);
    const releaseId = ref<number | null>(null);

    const csSubscriptions = ref<CodeSetSubscriptions | null>(null);

    type Subscription = {
        name: string;
        enabled: boolean;
    };

    const selectedSubscription = ref<Subscription | null>(null);
    const selectedScopes = ref<string[]>([]);

    watch(selectedSubscription, (oldValue, newValue) => {
        if (oldValue?.name === newValue?.name) return;
        selectedScopes.value = [];
    });

    const isSingleObjectMessagesEnabled = ref(false);

    const enabledSubscriptions = computed(() => {
        return csSubscriptions.value?.subscriptions.filter(s => s.enabled) ?? [];
    });

    const disabledSubscriptionsNamesLine = computed(() => {
        const names = csSubscriptions.value?.subscriptions
            .filter(s => !s.enabled)
            .map(s => s.name)
            ?? [];

        return names.length === 0 ? null : names.join(', ');
    });

    const facilities = ref<FacilityPublishEligibility[]>([]);

    // Global libraries are published to STID on the single GLOBAL scope, which needs no facility selection.
    const publishesToGlobalScope = computed(() => {
        return !!csSubscriptions.value?.isGlobal && selectedSubscription.value?.name === 'STID';
    });

    const scopes = computed(() => {
        if (!selectedSubscription.value
            || !csSubscriptions.value
            || !facilities.value) return null;

        if (publishesToGlobalScope.value) return null;

        const facilitiesForApp: FacilityPublishEligibility[] = [];

        switch (selectedSubscription.value.name) {
            case 'STID':
                facilitiesForApp.push(...facilities.value.filter(f => f.isForStid));
                break;
            case 'TIE':
                facilitiesForApp.push(...facilities.value.filter(f => f.isForTie));
                break;
            default:
                return null;
        }
        if (csSubscriptions.value.isGlobal)
            return facilitiesForApp;

        const validScopes = new Set(csSubscriptions.value.scopes.map(s => s.identity));
        return facilitiesForApp.filter(f => validScopes.has(f.identity));
    });

    onMounted(async () => {
        facilities.value = await getFacilityPublishEligibilityList();
    });

    watch(
        () => props.codeSetName,
        async (codeSetName) => {
            csSubscriptions.value = null;
            selectedScopes.value = [];
            selectedSubscription.value = null;
            isSingleObjectMessagesEnabled.value = false;

            if (!codeSetName) return;

            isLoading.value = true;
            try {
                csSubscriptions.value = await getCodeSetSubscriptions(http, codeSetName);
            } catch (error) {
                showError('Error fetching subscriptions');
            } finally {
                isLoading.value = false;
            }
        },
        { immediate: true }
    );

    watch(() => showModal.value,
          (newVal) => {
              if (!newVal) {
                  selectedScopes.value = [];
                  selectedSubscription.value = null;
                  isSingleObjectMessagesEnabled.value = false;
              }
          }
    );

    const canPublish = computed(() => {
        return !!props.codeSetName
            && (publishesToGlobalScope.value || selectedScopes.value.length > 0)
            && selectedSubscription.value
            && !isLoading.value
            && !isSubmitting.value;
    });

    function openPublishModal() {
        showModal.value = true;
    }

    function closePublishModal() {
        if (isSubmitting.value) return;
        showModal.value = false;
    }

    async function publish() {
        if (!canPublish.value) return;

        isSubmitting.value = true;
        try {
            const res = await createCodeSetPublishingRelease(http, {
                codeSetName: props.codeSetName,
                scopes: selectedScopes.value,
                application: selectedSubscription.value!.name!,
                isSingleObjectMessagesEnabled: isSingleObjectMessagesEnabled.value,
                regEx: props.codeFilter.regEx,
                queries: props.codeFilter.queries
            });
            showPublishProgress.value = true;
            releaseId.value = res.data.id;
        } catch (error) {
            showError(`Error publishing codeset ${error}`);
        } finally {
            isSubmitting.value = false;
        }
    }

    function onPublishSuccess() {
        releaseId.value = null;
        showPublishProgress.value = false;
        showModal.value = false;
    }

    const toolTipText = computed(() => {
        if (!props.codeSetName) {
            return '';
        } else if (isLoading.value) {
            return 'Loading subscriptions…';
        } else if (!csSubscriptions.value) {
            return 'No subscription information available';
        } else if (!csSubscriptions.value.isReleaseReady) {
            return 'Not ready for release';
        }
        return 'Publish library set';
    });
</script>

<template>
    <section>
        <b-tooltip
            :active="props.codeSetName && !!csSubscriptions"
            :label="toolTipText">
            <b-button
                v-require-can-edit-code="{ libraryName: props.libraryName }"
                icon-left="rocket-launch"
                :loading="isLoading"
                :disabled="isLoading || !props.codeSetName || !csSubscriptions?.isReleaseReady"
                @click="openPublishModal">
                Publish
            </b-button>
        </b-tooltip>
        <b-modal
            v-model="showModal"
            has-modal-card
            :can-cancel="!isSubmitting">
            <div class="modal-card">
                <header class="modal-card-head">
                    <div>
                        <h2 class="title is-4 mb-1">
                            <span class="icon has-text-info"><i class="mdi mdi-rocket-launch" /></span> Publish codeset
                        </h2>
                        <p class="has-text-grey is-size-7">
                            Codeset: <strong>{{ props.codeSetName ?? 'None' }}</strong>
                        </p>
                    </div>
                </header>

                <section class="modal-card-body">
                    <div
                        v-if="isLoading"
                        class="has-text-grey">
                        Loading subscriptions…
                    </div>
                    <div
                        v-else-if="!csSubscriptions || csSubscriptions.subscriptions.length === 0"
                        class="has-text-grey">
                        No subscription information available.
                    </div>
                    <div
                        v-else
                        class="is-5">
                        <generic-dropdown-menu
                            class="mb-4"
                            :alternatives="enabledSubscriptions"
                            display-property="name"
                            none-selected-text="Select receiver"
                            selected-text-prefix="Receiver: "
                            :is-loading="isLoading"
                            button-type="is-info"
                            :selected="selectedSubscription"
                            @setSelected="(newVal: Subscription | null) => selectedSubscription=newVal" />
                        <p
                            v-if="disabledSubscriptionsNamesLine"
                            class="has-text-grey is-size-5 mb-3">
                            Disabled subscriptions: <strong>{{ disabledSubscriptionsNamesLine }}</strong>
                        </p>
                        <div
                            v-if="selectedSubscription?.name === 'TIE'"
                            class="mb-4">
                            <b-checkbox v-model="isSingleObjectMessagesEnabled">
                                Single object TIE Messages
                            </b-checkbox>
                        </div>
                        <p
                            v-if="publishesToGlobalScope"
                            class="has-text-grey">
                            Global library: published to all applicable facilities in a single message using the
                            STID <strong>GLOBAL</strong> scope.
                        </p>
                        <div
                            v-else-if="scopes"
                            class="facilities-section">
                            <div class="facilities-header">
                                <h3 class="title is-5 mb-1">
                                    Facilities
                                    <span class="tag is-info is-light ml-2">
                                        {{ selectedScopes.length }}/{{ scopes.length }}
                                    </span>
                                </h3>
                            </div>
                            <multi-scope-selector
                                v-model:selected-scopes="selectedScopes"
                                :scopes="scopes" />

                            <p
                                v-if="scopes.length === 0"
                                class="has-text-grey is-size-7 mt-2">
                                No facilities available for this receiver.
                            </p>
                        </div>
                    </div>
                </section>

                <footer class="modal-card-foot is-justify-content-space-between">
                    <p class="has-text-grey is-size-5 mb-0">
                        Publishing
                        <strong>{{ props.filteredCodeCount.toLocaleString() }}</strong>
                        {{ props.filteredCodeCount === 1 ? 'code' : 'codes' }}
                        <template v-if="publishesToGlobalScope">
                            to the <strong>GLOBAL</strong> scope
                        </template>
                        <template v-else>
                            to
                            <strong>{{ selectedScopes.length.toLocaleString() }}</strong>
                            {{ selectedScopes.length === 1 ? 'facility' : 'facilities' }}
                        </template>
                        <strong>·</strong> Receiver:
                        <strong>{{ selectedSubscription?.name ?? 'no receiver' }}</strong>
                    </p>

                    <div>
                        <b-button
                            class="button mr-3"
                            variant="secondary"
                            :disabled="isSubmitting"
                            @click="closePublishModal">
                            Close
                        </b-button>

                        <b-button
                            class="button is-primary"
                            :disabled="!canPublish"
                            :loading="isSubmitting"
                            @click="publish">
                            Publish
                        </b-button>
                    </div>
                </footer>
            </div>
        </b-modal>
    </section>
    <code-edit-save-progress
        v-require-can-edit-code="{ libraryName: props.libraryName, scopes: selectedScopes }"
        :close-enabled="true"
        :release-id="releaseId"
        :save-process-started="showPublishProgress"
        @success="onPublishSuccess" />
</template>

<style scoped>
:deep(.modal-card) {
    max-height:  calc(100vh - 3vh);
}

:deep(.modal-card-body) {
    overflow: visible;
    max-height: none;
    flex: 0 0 auto;
}

.facilities-section {
    margin-top: 1rem;
    padding: 1rem;
    border: 1px solid hsl(0, 0%, 92%);
    border-radius: 8px;
    box-shadow: 0 1px 2px rgba(10, 10, 10, 0.04);
}

.facilities-header {
    border-bottom: 1px solid hsl(0, 0%, 92%);
    padding-bottom: 0.5rem;
    margin-bottom: 0.75rem;
}
</style>
